'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { MapPin, ArrowRight, Info, CheckCircle, Users, Star } from 'lucide-react';
import { FLORIDA_REGIONS, PUERTO_RICO_REGIONS } from '@/lib/catalogData';
import { usePlacementCatalog } from '@/hooks/usePlacementCatalog';
import { createClient } from '@/lib/supabase/client';

interface RegionOption {
  id: string;
  name: string;
  market: string;
  cities: string[];
  households: number;
}

const multiRegionDiscounts = [
  { regions: 2, discount: 0, label: '2 regions', note: 'Base rate' },
  { regions: 3, discount: 5, label: '3 regions', note: '5% off standard' },
  { regions: 5, discount: 10, label: '5+ regions', note: '10% off standard' },
  { regions: 8, discount: 15, label: '8+ regions', note: '15% off standard' },
];

export default function RegionsPricingContent() {
  const [market, setMarket] = useState<'florida' | 'puerto-rico'>('florida');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<'one' | 'three'>('one');
  const { placements, source } = usePlacementCatalog();

  const [allRegions, setAllRegions] = useState<RegionOption[]>([
    ...FLORIDA_REGIONS.map(r => ({ ...r, market: 'FL' })),
    ...PUERTO_RICO_REGIONS.map(r => ({ ...r, market: 'PR' })),
  ]);
  const [regionsLoading, setRegionsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadRegions = async () => {
      setRegionsLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('regions')
          .select('id, name, market, cities, households')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (cancelled) return;
        if (!error && data?.length) {
          setAllRegions(
            data.map((r: any) => ({
              id: r.id,
              name: r.name,
              market: r.market,
              cities: Array.isArray(r.cities) ? r.cities : [],
              households: r.households ?? 1000,
            }))
          );
        }
      } catch {
        // Non-fatal: static fallback remains
      } finally {
        if (!cancelled) setRegionsLoading(false);
      }
    };
    loadRegions();
    return () => { cancelled = true; };
  }, []);

  const marketCode = market === 'florida' ? 'FL' : 'PR';
  const regions = allRegions.filter(r => r.market === marketCode);

  const toggleRegion = (id: string) => {
    setSelectedRegions(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const getDiscount = (count: number) => {
    if (count >= 8) return 0.15;
    if (count >= 5) return 0.10;
    if (count >= 3) return 0.05;
    return 0;
  };

  const discount = getDiscount(selectedRegions.length);

  const calcTotal = () => {
    return placements.map(p => {
      const baseRate = selectedTerm === 'one' ? p.oneIssue : p.threeIssue;
      const rate = p.premium ? baseRate : Math.round(baseRate * (1 - discount));
      return { ...p, rate, total: rate * selectedRegions.length };
    });
  };

  const pricingData = calcTotal();

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-16">
      {/* Header */}
      <div className="mb-12">
        <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-3">Coverage & Rates</p>
        <h1 className="font-display text-4xl sm:text-5xl font-800 mb-4">
          <span className="text-white">Regions & </span>
          <span className="text-gradient">Pricing</span>
        </h1>
        <p className="text-slate-400 max-w-2xl">
          Select your target regions to see exact pricing. Each region receives 1,000 printed copies of ZipAdBook every 5 weeks.
        </p>
      </div>

      {/* Market toggle */}
      <div className="flex items-center gap-3 mb-8">
        <span className="text-sm text-slate-400 font-medium">Market:</span>
        <div className="flex p-1 glass-card rounded-xl">
          <button
            onClick={() => { setMarket('florida'); setSelectedRegions([]); }}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${market === 'florida' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            🌴 Florida
          </button>
          <button
            onClick={() => { setMarket('puerto-rico'); setSelectedRegions([]); }}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${market === 'puerto-rico' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
          >
            🇵🇷 Puerto Rico
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Left: Regions */}
        <div className="xl:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-700 text-white">
              {market === 'florida' ? 'Florida Regions' : 'Puerto Rico Regions'}
            </h2>
            {selectedRegions.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="chip chip-selected">{selectedRegions.length} selected</span>
                <button onClick={() => setSelectedRegions([])} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Clear all</button>
              </div>
            )}
          </div>

          {regionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-brand-500/30 border-t-brand-400 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {regions.map((region) => {
                const selected = selectedRegions.includes(region.id);
                return (
                  <button
                    key={`region-card-${region.id}`}
                    onClick={() => toggleRegion(region.id)}
                    className={`text-left glass-card rounded-2xl p-5 border transition-all duration-200 group ${
                      selected
                        ? 'border-brand-500/60 bg-brand-600/10 shadow-glow-sm'
                        : 'border-white/8 hover:border-brand-500/30 hover:bg-white/3'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={15} className={selected ? 'text-brand-400' : 'text-slate-500'} />
                        <span className="text-sm font-semibold text-white">{region.name}</span>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        selected ? 'bg-brand-500 border-brand-500' : 'border-white/20'
                      }`}>
                        {selected && <CheckCircle size={12} className="text-white" />}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {region.cities.slice(0, 3).map((city) => (
                        <span key={`city-rp-${region.id}-${city}`} className="chip" style={{ fontSize: '10px' }}>{city}</span>
                      ))}
                      {region.cities.length > 3 && (
                        <span className="chip" style={{ fontSize: '10px' }}>+{region.cities.length - 3} more</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Users size={11} />
                      <span>{region.households.toLocaleString()} copies / issue</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Multi-region discount table */}
          <div className="glass-card rounded-2xl p-6 border border-brand-600/20">
            <div className="flex items-center gap-2 mb-4">
              <Star size={16} className="text-brand-400" />
              <h3 className="font-semibold text-white text-sm">Standard Inside Page — Multi-Region Discounts</h3>
              <div className="ml-auto">
                <Info size={14} className="text-slate-500" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {multiRegionDiscounts.map((tier) => (
                <div
                  key={`discount-tier-${tier.regions}`}
                  className={`rounded-xl p-3 text-center border transition-all ${
                    selectedRegions.length >= tier.regions && (tier.regions === multiRegionDiscounts[multiRegionDiscounts.length - 1].regions || selectedRegions.length < (multiRegionDiscounts[multiRegionDiscounts.indexOf(tier) + 1]?.regions ?? 999))
                      ? 'bg-brand-600/20 border-brand-500/40' : 'bg-navy-700/50 border-white/8'
                  }`}
                >
                  <p className="text-sm font-bold text-white tabular-nums">{tier.discount > 0 ? `-${tier.discount}%` : 'Base'}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{tier.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{tier.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Pricing summary */}
        <div className="xl:col-span-1 space-y-4">
          {/* Term toggle */}
          <div className="glass-card rounded-2xl p-5">
            <h3 className="font-semibold text-white text-sm mb-3">Ad Term</h3>
            <div className="flex p-1 bg-navy-800 rounded-xl">
              <button
                onClick={() => setSelectedTerm('one')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${selectedTerm === 'one' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
              >
                One Issue
              </button>
              <button
                onClick={() => setSelectedTerm('three')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${selectedTerm === 'three' ? 'bg-gradient-primary text-white' : 'text-slate-400 hover:text-white'}`}
              >
                3-Issue / mo
              </button>
            </div>
          </div>

          {/* Pricing breakdown */}
          <div className="glass-card rounded-2xl p-5 border border-brand-600/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white text-sm">Pricing Breakdown</h3>
              {selectedRegions.length > 0 && discount > 0 && (
                <span className="text-xs font-semibold text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded-full">
                  {(discount * 100).toFixed(0)}% off applied
                </span>
              )}
            </div>

            {selectedRegions.length === 0 ? (
              <div className="text-center py-8">
                <MapPin size={32} className="text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Select regions to see pricing</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pricingData.map((p) => (
                  <div key={`pricing-row-${p.id}`} className="flex items-center justify-between py-2 border-b border-white/6 last:border-0">
                    <div>
                      <p className="text-xs font-medium text-white">{p.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        ${p.rate.toLocaleString()} × {selectedRegions.length} region{selectedRegions.length > 1 ? 's' : ''}
                        {selectedTerm === 'three' ? '/mo' : ''}
                      </p>
                    </div>
                    <p className="font-display font-bold text-base text-white tabular-nums">
                      ${p.total.toLocaleString()}
                      {selectedTerm === 'three' && <span className="text-xs text-slate-400">/mo</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {source === 'fallback' && (
            <p className="text-xs text-slate-500">
              Showing fallback catalog values. Admin placement catalog is not populated yet.
            </p>
          )}

          {/* CTA */}
          <Link
            href={`/order-wizard`}
            className="flex items-center justify-center gap-2 w-full py-3.5 text-sm font-semibold btn-primary"
          >
            Book Selected Regions
            <ArrowRight size={16} />
          </Link>

          <p className="text-xs text-slate-500 text-center">
            Prices shown per region per issue. Volume discounts apply to standard inside pages only.
          </p>
        </div>
      </div>
    </div>
  );
}